import React, { useState, useRef, useEffect } from "react";
import { sendMessageToAI } from "./aiChatServices";
import "./AiChat.css";
import { useUserType } from "../../contexts/UserTypeContext";
import AdminIconSend from "../../assets/Orange-icons/Send.svg";
import DonorIconSend from "../../assets/Donor/Donor-Send.svg";
import CmsLogo from "../../assets/image/CMS_LOGO.svg";
import { sanitizeHtml } from "../../utils/sanitizeHtml";

const TypeWriter = ({ text, speed = 15, onComplete, shouldAnimate = true }) => {
  const [displayText, setDisplayText] = useState(shouldAnimate ? "" : text);
  const [currentIndex, setCurrentIndex] = useState(shouldAnimate ? 0 : text.length);
  const [hasCompleted, setHasCompleted] = useState(!shouldAnimate);
  const [isAnimating, setIsAnimating] = useState(shouldAnimate);

  useEffect(() => {
    if (!shouldAnimate) {
      setDisplayText(text);
      setCurrentIndex(text.length);
      setHasCompleted(true);
      setIsAnimating(false);
      return;
    }

    if (hasCompleted) {
      return;
    }

    setIsAnimating(true);
  }, [shouldAnimate, text, hasCompleted]);

  useEffect(() => {
    if (!isAnimating || hasCompleted || !shouldAnimate) {
      return;
    }

    if (currentIndex < text.length) {
      const timer = setTimeout(() => {
        setDisplayText(text.slice(0, currentIndex + 1));
        setCurrentIndex(currentIndex + 1);
      }, speed);

      return () => clearTimeout(timer);
    } else if (currentIndex >= text.length) {
      setHasCompleted(true);
      setIsAnimating(false);
      if (onComplete) onComplete();
    }
  }, [currentIndex, text, speed, onComplete, isAnimating, hasCompleted, shouldAnimate]);

  const formatMessage = (content) => {
    return content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/# (.*?)(\n|$)/g, '<h3>$1</h3>')
      .replace(/## (.*?)(\n|$)/g, '<h4>$1</h4>')
      .replace(/### (.*?)(\n|$)/g, '<h5>$1</h5>')
      .replace(/••(.*?)(\n|$)/g, '<br><strong>$1</strong><br>')
      .replace(/• (.*?)(\n|$)/g, '&nbsp;&nbsp;• $1<br>')
      .replace(/> (.*?)(\n|$)/g, '<blockquote>$1</blockquote>')
      .replace(/\n/g, '<br>');
  };

  // SECURITY (hardening 2026-04-20): AI responses are attacker-influenceable
  // via prompt injection — if a customer adds `<script>…` to a note the
  // model may echo it back. Run the markdown-expanded HTML through
  // DOMPurify (rich allowlist: basic formatting + headings + blockquote
  // + links) before it hits the DOM.
  return (
    <div
      dangerouslySetInnerHTML={{
        __html: sanitizeHtml(formatMessage(displayText))
      }}
    />
  );
};

const ChatIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#fff"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);

const SendIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="22" y1="2" x2="11" y2="13"></line>
    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
  </svg>
);

const DeleteIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 6h18"></path>
    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
  </svg>
);

const BackIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M19 12H5"></path>
    <path d="M12 19l-7-7 7-7"></path>
  </svg>
);

const AssistantIcon = ({ isDonationMode = false }) => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect 
      width="32" 
      height="32" 
      rx="16" 
      fill={isDonationMode ? "#5BB5C9" : "#0f2b46"}
    />
    <path
      d="M22 12.5C22 11.12 20.88 10 19.5 10H12.5C11.12 10 10 11.12 10 12.5V19.5C10 20.88 11.12 22 12.5 22H19.5C20.88 22 22 20.88 22 19.5V12.5ZM16 20C14.34 20 13 18.66 13 17C13 15.34 14.34 14 16 14C17.66 14 19 15.34 19 17C19 18.66 17.66 20 16 20ZM16 12C16.83 12 17.5 12.67 17.5 13.5C17.5 14.33 16.83 15 16 15C15.17 15 14.5 14.33 14.5 13.5C14.5 12.67 15.17 12 16 12Z"
      fill="white"
    />
  </svg>
);

const PREDEFINED_QUESTIONS = {
  JOB_MANAGER: [
    {
      question: "What can I do as a Company Admin?",
      answer: `# Company Admin Capabilities

As a Company Admin, you have the highest level of access within the contract and donation management system.

••Key Features

1. User Management
   • Add, edit, or remove staff members
   • Assign or modify user roles
   • Reset user passwords
   • View activity logs

2. Company Settings
   • Configure company profile
   • Set business hours
   • Manage notifications
   • Customize branding

3. Customer Management
   • View customer profiles
   • Manage service locations
   • Track communications
   • Set up categories

4. Financial Controls
   • Configure payment gateways
   • Set pricing structures
   • Manage billing cycles
   • Monitor transactions

••Additional Capabilities

• System-wide configuration
• Integration management
• Security controls
• Performance monitoring` 
    },
    {
      question: "What features are available for Staff Members?",
      answer: `# Staff Member Features

Staff Members have role-based access to system features. Here's what's typically available:

••Core Features

1. Customer Management
   • View customer profiles
   • Add new customers
   • Manage service addresses
   • Track communications

2. Quote System
   • Create and edit quotes
   • Add materials and labor
   • Submit for approval
   • Track quote history

3. Contract Management
   • Generate contracts
   • View contract status
   • Collect signatures
   • Monitor progress

••Additional Tools

• Schedule management
• Payment processing
• Document access
• Customer support`
    }
  ],
  DONATION: [
    {
      question: "What can I do as a Donor?",
      answer: `# Donor Portal Features

Welcome to your Donor Portal! Here's everything you can do:

••Core Functions

1. Donation Management
   • Make one-time gifts
   • Set up recurring donations
   • Choose payment methods
   • Manage preferences

2. Account Features
   • View donation history
   • Access tax receipts
   • Update information
   • Set notifications

3. Pledge Management
   • Create pledges
   • Track fulfillment
   • Modify commitments
   • View history

••Additional Tools

• Generate statements
• Track impact
• View campaign progress
• Access support`
    },
    {
      question: "What reports are available?",
      answer: `# Available Reports

Access comprehensive reporting tools to track your giving impact.

••Report Types

1. Donation Summary
   • Total contributions
   • Giving frequency
   • Payment methods
   • Campaign participation

2. Transaction History
   • Payment records
   • Processing dates
   • Payment status
   • Receipt copies

3. Tax Documentation
   • Annual statements
   • Deduction records
   • In-kind donations
   • Special contributions

••Analytics

• Giving patterns
• Donation trends
• Impact metrics
• Goal tracking`
    },
    {
      question: "How do I manage recurring donations?",
      answer: `# Managing Recurring Donations

Learn how to set up and manage your recurring contributions.

••Setup Process

1. Initial Configuration
   • Choose amount
   • Select frequency
   • Pick payment method
   • Set start date

2. Payment Settings
   • Enter details
   • Verify information
   • Set preferences
   • Configure alerts

3. Management Tools
   • View schedule
   • Modify amounts
   • Adjust dates
   • Update frequency

••Additional Options

• Change payment methods
• Update contact info
• Pause donations
• Cancel recurring gifts`
    }
  ]
};

export default function AIChat({ isOpen, onClose }) {
  const [closing, setClosing] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [typingMessageId, setTypingMessageId] = useState(null);
  const [completedAnimations, setCompletedAnimations] = useState(new Set());
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const inputContainerRef = useRef(null);
  const chatContainerRef = useRef(null);
  const { userType } = useUserType();
  const isDonationMode = userType === "donor";
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [messages, isOpen]);

  // When chat closes, stop any ongoing typing animation and mark messages as complete
  useEffect(() => {
    if (!isOpen && messages.length > 0) {
      // Mark any typing message as complete to prevent re-animation on reopen
      setTypingMessageId(null);
      setMessages(prevMessages =>
        prevMessages.map(m =>
          m.shouldAnimate ? { ...m, isTyping: false, shouldAnimate: false } : m
        )
      );
      // Add all message ids to completed animations
      setCompletedAnimations(prev => {
        const newSet = new Set(prev);
        messages.forEach(m => {
          if (m.id) newSet.add(m.id);
        });
        return newSet;
      });
    }
  }, [isOpen]);

  // Close chat when clicking outside the chat container
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        chatContainerRef.current &&
        !chatContainerRef.current.contains(event.target)
      ) {
        handleToggleChat();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleToggleChat = () => {
    if (isOpen) {
      setClosing(true);
      setTimeout(() => {
        onClose();
        setClosing(false);
      }, 300);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInput = (e) => {
    setInput(e.target.value);
    autoGrowTextarea(e.target);
  };

  const autoGrowTextarea = (el) => {
    el.style.height = "44px";
    const newHeight = Math.min(el.scrollHeight, 120);
    el.style.height = newHeight + "px";

    if (inputContainerRef.current) {
      inputContainerRef.current.style.paddingBottom =
        newHeight > 56 ? "12px" : "20px";
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    if (textareaRef.current) {
      textareaRef.current.style.height = "44px";
      if (inputContainerRef.current) {
        inputContainerRef.current.style.paddingBottom = "20px";
      }
    }

    const userMsg = { role: "user", content: input.trim() };
    setMessages((msgs) => [...msgs, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const aiContent = await sendMessageToAI(input.trim());
      const messageId = Date.now();
      const newAssistantMessage = {
        role: "assistant",
        content: aiContent,
        id: messageId,
        isTyping: true,
        shouldAnimate: true
      };
      
      setMessages((msgs) => [...msgs, newAssistantMessage]);
      setTypingMessageId(messageId);
    } catch (err) {
      const messageId = Date.now();
      const errorMessage = {
        role: "assistant",
        content: "I apologize, but I encountered an error. Please try again.",
        id: messageId,
        isTyping: true,
        shouldAnimate: true
      };
      
      setMessages((msgs) => [...msgs, errorMessage]);
      setTypingMessageId(messageId);
    }
    setLoading(false);
  };

  const handleDeleteChat = () => {
    setMessages([]);
    setCompletedAnimations(new Set());
    setTypingMessageId(null);
  };

  const handleQuestionClick = async (question) => {
    setInput(question);
    
    if (textareaRef.current) {
      textareaRef.current.style.height = "44px";
      if (inputContainerRef.current) {
        inputContainerRef.current.style.paddingBottom = "20px";
      }
    }

    const userMsg = { role: "user", content: question.trim() };
    setMessages((msgs) => [...msgs, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const aiContent = await sendMessageToAI(question.trim());
      const messageId = Date.now();
      const newAssistantMessage = {
        role: "assistant",
        content: aiContent,
        id: messageId,
        isTyping: true,
        shouldAnimate: true
      };
      
      setMessages((msgs) => [...msgs, newAssistantMessage]);
      setTypingMessageId(messageId);
    } catch (err) {
      const messageId = Date.now();
      const errorMessage = {
        role: "assistant",
        content: "I apologize, but I encountered an error. Please try again.",
        id: messageId,
        isTyping: true,
        shouldAnimate: true
      };
      
      setMessages((msgs) => [...msgs, errorMessage]);
      setTypingMessageId(messageId);
    }
    setLoading(false);
  };

  return (
    <>
      {(isOpen || closing) && (
        <div
          ref={chatContainerRef}
          className={`chat-container ${closing ? "closing" : ""} ${
            isDonationMode ? "donor-mode" : ""
          }`}
        >
          <div
            className={`chat-header ${
              isDonationMode ? "bg-donor-color" : "bg-blue-color"
            }`}
          >
            <button
              onClick={handleToggleChat}
              className="back-button-mobile"
              aria-label="Go back"
            >
              <BackIcon />
            </button>
            <div
              className={`chat-header-title ${
                isDonationMode ? "bg-donor-color" : "bg-blue-color"
              }`}
            >
              <AssistantIcon isDonationMode={isDonationMode} />
              <span style={{ fontSize: '16px' }}
              >
                {isDonationMode ? "Cloud Donor Manager Assistant" : "Cloud Job Manager Assistant"}
              </span>
            </div>
            <div className="chat-actions">
              <button
                onClick={handleDeleteChat}
                className="delete-button"
                aria-label="Delete chat"
              >
                <DeleteIcon />
              </button>
              <button
                onClick={handleToggleChat}
                className="close-button"
                aria-label="Close chat"
              >
                ×
              </button>
            </div>
          </div>
          <div className="messages-container">
            {messages.length === 0 && (
                              <div className="welcome-message">
                  <div style={{
                     backgroundColor: isDonationMode ? "#5BB5C9" : "#0f2b46",
                     borderRadius: '50%',
                     padding: '15px',
                     width: '60px',
                     height: '60px',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center'
                   }}>
                    <AssistantIcon isDonationMode={isDonationMode} />
                  </div>
                <h3 className={`${
                    isDonationMode ? "text-donor-color" : "text-blue-color"
                  }`}>
                  Welcome to{" "}
                  {isDonationMode
                    ? "Cloud Donor Manager Assistant"
                    : "Cloud Job Manager Assistant"}
                </h3>
                <p>
                  {isDonationMode
                    ? "Hi! I'm your Cloud Donor Manager assistant, here to help you manage donations, pledges, and answer questions about our donation system."
                    : "Hi! I'm your Cloud Job Manager assistant, ready to help you manage customers, quotes, contracts, and invoices efficiently."}
                </p>
                <div className="suggested-questions">
                  <p className="questions-title">Popular Questions:</p>
                  {(isDonationMode
                    ? PREDEFINED_QUESTIONS.DONATION
                    : PREDEFINED_QUESTIONS.JOB_MANAGER
                  ).map((item, index) => (
                    <button
                      key={index}
                      className={`question-button ${
                        isDonationMode ? "text-donor-color" : "text-blue-color"
                      }`}

                      onClick={() => handleQuestionClick(item.question)}
                    >
                      {item.question}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`message ${
                  msg.role === "user" ? "user-message" : "ai-message"
                }`
                
              }
          
              >
                {msg.role === "assistant" && (
                  <div 
                    className="message-avatar" 
                    style={{ 
                      backgroundColor: isDonationMode ? "#5BB5C9" : "#1e3a5f" 
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                    </svg>
                  </div>
                )}
                <div className="message-content">
                  {msg.role === "assistant" ? (
                    <TypeWriter
                      text={msg.content}
                      speed={15}
                      shouldAnimate={msg.shouldAnimate && !completedAnimations.has(msg.id) && typingMessageId === msg.id}
                      onComplete={() => {
                        setTypingMessageId(null);
                        setCompletedAnimations(prev => new Set([...prev, msg.id]));
                        setMessages(prevMessages =>
                          prevMessages.map(m =>
                            m.id === msg.id
                              ? { ...m, isTyping: false, shouldAnimate: false }
                              : m
                          )
                        );
                      }}
                    />
                  ) : (
                    <TypeWriter
                    className={`${
                      isDonationMode ? "bg-green-color" : "bg-orange-color"
                    }`}
                      text={msg.content}
                      shouldAnimate={false}
                    />
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="message ai-message">
                <div 
                  className="message-avatar"
                  style={{ 
                    backgroundColor: isDonationMode ? "#5BB5C9" : "#1e3a5f" 
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                </div>
                <div className="message-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <div className="typing-indicator" style={{ marginBottom: '4px' }}>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
                    Assistant is typing...
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          <div
            className={`input-container ${
              isDonationMode ? "border-green-color" : "border-orange-color"
            }`}
            ref={inputContainerRef}
          >
            <textarea
              ref={textareaRef}
              value={input}
              onChange={handleInput}
              onKeyDown={handleKeyDown}
              placeholder="Type your message..."
              disabled={loading}
              className={`chat-input ${
                isDonationMode ? "border-green-color" : "border-orange-color"
              }`}
              rows="1"
            />
            <button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              className={`send-arrow ${
                isDonationMode ? "border-donor-color" : "border-orange-color"
              }`}
              aria-label="Send message"
            >
              <img
                src={isDonationMode ? DonorIconSend : AdminIconSend}
                alt="Send"
              />
            </button>
          </div>
        </div>
      )}
    </>
  );
}